import { QrCode } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

type ScanEvent = {
  id: string;
  created_at: string;
  scan_method: string;
  scan_context: string;
  workflow_stage?: string | null;
  scanned_by?: { id: string; first_name?: string; last_name?: string; email?: string } | null;
  asset_instance?: { id: string; instance_number?: string; template?: { name?: string } } | null;
  qr_code?: { id: string; code?: string } | null;
  task?: { id: string; task_number?: string; task_type?: string } | null;
  shipment?: { id: string; bl_number?: string } | null;
};

type ScanHistoryCardProps = {
  title: string;
  endpoint: string;
  emptyMessage?: string;
};

function ScanHistoryCard({ title, endpoint, emptyMessage = 'No scans recorded' }: ScanHistoryCardProps) {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ScanEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (mounted && data.success && Array.isArray(data.data)) setEvents(data.data);
      } catch {
        if (mounted) setEvents([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [endpoint]);

  if (loading) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription>QR scans recorded for this context</CardDescription>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
        <div className="space-y-3">
          {events.map((e) => (
            <div
              key={e.id}
              className="flex flex-wrap items-center gap-2 text-sm border-b border-border pb-3 last:border-0 last:pb-0"
            >
              <span className="text-muted-foreground">
                {new Date(e.created_at).toLocaleString()}
              </span>
              {e.workflow_stage && (
                <Badge variant="outline" className="text-xs">{e.workflow_stage}</Badge>
              )}
              {e.asset_instance && (
                <button
                  type="button"
                  onClick={() => navigate(`/units/${e.asset_instance!.id}`)}
                  className="text-primary hover:underline font-mono"
                >
                  {e.asset_instance.instance_number || e.asset_instance.template?.name || 'Unit'}
                </button>
              )}
              {e.qr_code && (
                <button
                  type="button"
                  onClick={() => navigate(`/qr-codes/${e.qr_code!.id}`)}
                  className="inline-flex items-center gap-1 text-muted-foreground hover:text-primary"
                  title="View QR code"
                >
                  <QrCode className="h-3 w-3" />
                  {e.qr_code.code}
                </button>
              )}
              {e.scanned_by && (
                <span className="text-muted-foreground">
                  by {e.scanned_by.first_name && e.scanned_by.last_name
                    ? `${e.scanned_by.first_name} ${e.scanned_by.last_name}`
                    : e.scanned_by.email}
                </span>
              )}
            </div>
          ))}
        </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ScanHistoryCard;
